import React from "react";
import { Link } from "react-router-dom";
import { useAccess } from "../hooks/useAccess";
import { landingPath } from "../nav";

// Route-level gate for a section or page. Wraps the route element in
// router.tsx so a bookmark or typed URL to a page the person's groups do not
// cover renders <AccessDenied/> instead of mounting the page and firing a
// burst of 403s at the backend (which is the REAL gate — this is the polite
// front door).
//
// Modes mirror the backend: in `enforce` a missing grant denies; in `shadow`
// the page renders with an amber strip saying it WILL be restricted, so the
// matrix can be validated by the people it affects before it is switched on;
// `off` (or a /me that could not be loaded) shows the page as before.

export const AccessDenied: React.FC<{ feature: string; label?: string }> = ({ feature, label }) => {
  const { me, can, preview } = useAccess();
  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center font-montserrat">
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-surface-sunken">
        <svg viewBox="0 0 24 24" className="h-7 w-7 text-content-subtle" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <h2 className="text-lg font-bold text-content">This page isn&apos;t in your groups</h2>
      <p className="mt-2 text-sm text-content-muted">
        {label ? <>{label} needs </> : <>It needs </>}
        the <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs">{feature}</code> feature.
        {me?.groups?.length ? (
          <> You&apos;re in {me.groups.join(", ")}.</>
        ) : (
          <> Your account isn&apos;t in any group yet.</>
        )}
        {preview && <> (Previewing as {preview.label}.)</>}
      </p>
      <p className="mt-1 text-xs text-content-subtle">Ask a super admin to add you in Admin → Users &amp; Groups.</p>
      <Link
        to={landingPath(can)}
        className="mt-6 inline-block rounded bg-qw-teal px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:opacity-90"
      >
        Go to your home page
      </Link>
    </div>
  );
};

// `feature` may be a LIST: the person needs ANY of them (a section is open to
// whoever can open at least one of its pages).
const RequireFeature: React.FC<{ feature: string | string[]; label?: string; children: React.ReactNode }> = ({ feature, label, children }) => {
  const { mode, can } = useAccess();
  const feats = Array.isArray(feature) ? feature : [feature];
  const allowed = feats.some((f) => can(f));
  const shown = feats[0];
  if (mode === "enforce" && !allowed) return <AccessDenied feature={shown} label={label} />;
  if (mode === "shadow" && !allowed) {
    return (
      <>
        <div
          role="status"
          className="border-b border-status-warn/40 bg-status-warn-soft px-4 py-2 text-center text-xs text-status-warn"
        >
          Access preview: this page will be hidden for your groups once access enforcement is switched on
          (feature <code>{shown}</code>). Nothing changes for you today.
        </div>
        {children}
      </>
    );
  }
  return <>{children}</>;
};

export default RequireFeature;
